import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { Button } from './Button';
import { cx } from './cx';
import { Icon } from './Icon';

/**
 * A question mark that explains the control beside it, on request.
 *
 * A `title` attribute was tried first: it shows after a second of hovering and never on a
 * click, which on a dispatch workstation is a tooltip nobody finds. This opens a note on
 * the click, where the person's attention already is, and closes it on Escape or on a
 * click anywhere else. The note is text, not a dialog — nothing is trapped and nothing
 * else goes inert.
 *
 * The note is placed against the window, not the card it sits in. A card clips what is
 * inside it for its rounded corners, so a note hung inside one was cut off the moment it
 * grew taller than the card — the order form's keys did, once the entrance letters were
 * added. Fixed, it is drawn whole whatever holds it, and follows the question mark when the
 * column scrolls.
 */
export function Hint({
  label,
  children,
  align = 'start',
  wide = false,
}: {
  label: string;
  children: ReactNode;
  /**
   * Which edge of the question mark the note hangs from. `end` for one at the right-hand end
   * of a header, whose note would otherwise run off the card.
   */
  align?: 'start' | 'end';
  /** A little wider, for a note that is a list rather than a sentence or two. */
  wide?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const id = useId();
  const [place, setPlace] = useState<CSSProperties | null>(null);

  // Where the note goes, from where the question mark is now — again on every scroll of
  // anything and on a resize, since a fixed note does not move with its column by itself.
  useLayoutEffect(() => {
    if (!open) return;

    const measure = () => {
      if (root.current === null) return;

      const mark = root.current.getBoundingClientRect();
      const top = mark.bottom + 6;

      setPlace({
        top,
        maxHeight: window.innerHeight - top - 8,
        ...(align === 'end' ? { right: window.innerWidth - mark.right } : { left: mark.left }),
      });
    };

    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // Captured and stopped, because a hint inside a dialog or over an enlarged map
      // would otherwise close those too with the one key.
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };

    const onPress = (event: MouseEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onPress, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onPress, true);
    };
  }, [open]);

  return (
    <span ref={root} className="relative inline-flex">
      <Button
        icon
        size="sm"
        variant="quiet"
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="help" small />
      </Button>
      {open && place !== null && (
        <span
          id={id}
          role="note"
          style={place}
          className={cx(
            'fixed overflow-y-auto p-2.5 rounded-card border border-rule bg-surface text-ink shadow-float text-secondary leading-[1.4] z-30',
            wide ? 'w-80' : 'w-72',
          )}
        >
          {children}
        </span>
      )}
    </span>
  );
}
